// ==========================================================================
// lib/calendar.ts — family calendar data + repeat expansion (FF-020, 9 Oct 2026)
// Events are stored once; repeating ones are expanded here for the range on
// screen. All-day events are plain dates; timed events are shown in Brisbane
// time (the household timezone).
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, addMonths, addWeeks, addYears, format, parseISO } from "date-fns";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";

export type Repeat = "none" | "daily" | "weekly" | "fortnightly" | "monthly" | "yearly";
export type CalEvent = {
  id: string; title: string; notes: string | null; location: string | null; all_day: boolean;
  start_date: string | null; end_date: string | null; starts_at: string | null; ends_at: string | null;
  repeat: Repeat; repeat_until: string | null;
  event_people: { person_id: string }[];
  event_exceptions: { occurs_on: string; cancelled: boolean }[];
  // Shown but not stored as events (025): public holidays and contacts' birthdays. Read-only.
  virtual?: "holiday" | "birthday"; colour?: string; contact_id?: string;
};
/** part > 0: a later day of a multi-day all-day event (it can't be dragged on its own). */
export type Occurrence = { key: string; event: CalEvent; date: string; start: Date | null; end: Date | null; people: string[]; part?: number };

export const REPEATS: { key: Repeat; label: string }[] = [
  { key: "none", label: "Doesn't repeat" }, { key: "daily", label: "Every day" }, { key: "weekly", label: "Every week" },
  { key: "fortnightly", label: "Every fortnight" }, { key: "monthly", label: "Every month" }, { key: "yearly", label: "Every year" },
];

export const ymd = (d: Date) => format(d, "yyyy-MM-dd");

function step(d: Date, r: Repeat, n: number): Date {
  switch (r) {
    case "daily": return addDays(d, n);
    case "weekly": return addWeeks(d, n);
    case "fortnightly": return addWeeks(d, 2 * n);
    case "monthly": return addMonths(d, n);
    case "yearly": return addYears(d, n);
    default: return d;
  }
}

/** All occurrences that start between from and to (inclusive, yyyy-MM-dd). */
export function expand(events: CalEvent[], from: string, to: string): Occurrence[] {
  const out: Occurrence[] = [];
  for (const e of events) {
    const baseStart = e.all_day ? parseISO(e.start_date!) : new Date(e.starts_at!);
    const durMs = e.all_day ? 0 : e.ends_at ? new Date(e.ends_at).getTime() - baseStart.getTime() : 0;
    const cancelled = new Set(e.event_exceptions.filter((x) => x.cancelled).map((x) => x.occurs_on));
    const until = e.repeat_until ?? "9999-12-31";
    for (let n = 0; n < 2000; n++) {
      const s = e.repeat === "none" && n > 0 ? null : step(baseStart, e.repeat, n);
      if (!s) break;
      const date = ymd(s);
      if (date > to || date > until) break;
      // All-day events can span days (e.g. "James away" 11–20 Oct): one entry per day.
      const span = e.all_day && e.end_date && e.end_date > e.start_date! ? Math.round((parseISO(e.end_date).getTime() - parseISO(e.start_date!).getTime()) / 86400000) : 0;
      for (let k = 0; k <= span; k++) {
        const day = k ? ymd(addDays(s, k)) : date;
        if (day >= from && day <= to && !cancelled.has(date)) {
          out.push({
            key: `${e.id}:${day}`, event: e, date: day, part: k || undefined,
            start: e.all_day ? null : s, end: e.all_day ? null : durMs ? new Date(s.getTime() + durMs) : null,
            people: e.event_people.map((p) => p.person_id),
          });
        }
      }
      if (e.repeat === "none") break;
    }
  }
  return out.sort((a, b) => a.date.localeCompare(b.date) || (a.start ? a.start.getTime() : 0) - (b.start ? b.start.getTime() : 0));
}

/** First and last day an occurrence covers (yyyy-MM-dd, Brisbane). */
export function spanOf(o: Occurrence): { first: string; last: string } {
  const e = o.event;
  if (e.all_day) {
    const first = o.part ? ymd(addDays(parseISO(o.date), -o.part)) : o.date;
    const days = e.end_date && e.start_date && e.end_date > e.start_date ? Math.round((parseISO(e.end_date).getTime() - parseISO(e.start_date).getTime()) / 86400000) : 0;
    return { first, last: days ? ymd(addDays(parseISO(first), days)) : first };
  }
  const last = o.end ? o.end.toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" }) : o.date;
  return { first: o.date, last: last > o.date ? last : o.date };
}

/** Runs over more than one day. */
export const isMultiDay = (o: Occurrence) => { const { first, last } = spanOf(o); return last > first; };

export function useEvents() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["events", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("calendar_events")
        .select("id,title,notes,location,all_day,start_date,end_date,starts_at,ends_at,repeat,repeat_until,event_people(person_id),event_exceptions(occurs_on,cancelled)")
        .eq("household_id", hid).is("deleted_at", null);
      if (error) throw error;
      return data as unknown as CalEvent[];
    },
  });
}

/** Public holidays and contacts' birthdays, as read-only calendar entries (025). */
export function useCalendarExtras() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["events", hid, "extras"], enabled: !!hid,
    queryFn: async () => {
      const [h, c] = await Promise.all([
        supabase.from("public_holidays").select("id,region,holiday_on,name,part_day").eq("household_id", hid),
        supabase.from("contacts").select("id,name,birthday_day,birthday_month,birth_year").eq("household_id", hid).is("archived_at", null).eq("show_birthday", true).not("birthday_month", "is", null),
      ]);
      if (h.error) throw h.error; if (c.error) throw c.error;
      const base = { notes: null, location: null, all_day: true, end_date: null, starts_at: null, ends_at: null, repeat_until: null, event_people: [], event_exceptions: [] };
      const holidays: CalEvent[] = (h.data as { id: string; region: string; holiday_on: string; name: string; part_day: string | null }[]).map((x) => ({
        ...base, id: `holiday-${x.id}`, title: `${x.name}${x.part_day ? ` (${x.part_day})` : ""}`, start_date: x.holiday_on, repeat: "none" as Repeat, virtual: "holiday" as const, colour: "warning",
      }));
      // A birthday repeats every year from the first one on or after 2000 (or the birth year when known).
      const birthdays: CalEvent[] = (c.data as { id: string; name: string; birthday_day: number | null; birthday_month: number | null; birth_year: number | null }[]).filter((x) => x.birthday_day && x.birthday_month).map((x) => ({
        ...base, id: `birthday-${x.id}`, title: `${x.name}'s birthday`, notes: x.birth_year ? String(x.birth_year) : null,
        start_date: `${x.birth_year ?? 2000}-${String(x.birthday_month!).padStart(2, "0")}-${String(x.birthday_day!).padStart(2, "0")}`,
        repeat: "yearly" as Repeat, virtual: "birthday" as const, colour: "chart-5", contact_id: x.id,
      }));
      return [...holidays, ...birthdays];
    },
  });
}

/** Title with the age for a birthday when the year is known ("Sarah's birthday · 35"). */
export const occTitle = (o: Occurrence) => (o.event.virtual === "birthday" && o.event.notes ? `${o.event.title} · ${Number(o.date.slice(0, 4)) - Number(o.event.notes)}` : o.event.title);

export type EventDraft = {
  title: string; notes: string; location: string; all_day: boolean;
  date: string; startTime: string; endTime: string; repeat: Repeat; repeat_until: string; people: string[];
  /** Last day for something that runs over several days (a booking, a trip). "" = same day. */
  endDate: string;
};

/** "2026-10-10" + "09:30" in Brisbane (UTC+10, no daylight saving) -> ISO. */
const brisbane = (date: string, time: string) => `${date}T${time}:00+10:00`;

export function useSaveEvent() {
  const hid = useHouseholdId(); const me = useMe(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, d }: { id?: string; d: EventDraft }): Promise<string> => {
      const row = {
        household_id: hid, title: d.title.trim(), notes: d.notes.trim() || null, location: d.location.trim() || null,
        all_day: d.all_day,
        start_date: d.all_day ? d.date : null, end_date: d.all_day && d.endDate > d.date ? d.endDate : null,
        starts_at: d.all_day ? null : brisbane(d.date, d.startTime || "09:00"),
        ends_at: d.all_day || !d.endTime ? null : brisbane(d.endDate > d.date ? d.endDate : d.date, d.endTime),
        repeat: d.repeat, repeat_until: d.repeat !== "none" && d.repeat_until ? d.repeat_until : null,
      };
      let eventId = id;
      if (id) {
        const { error } = await supabase.from("calendar_events").update(row).eq("id", id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("calendar_events").insert({ ...row, created_by: me?.id ?? null }).select("id").single();
        if (error) throw error;
        eventId = (data as { id: string }).id;
      }
      const { data: cur, error: e1 } = await supabase.from("event_people").select("person_id").eq("event_id", eventId!);
      if (e1) throw e1;
      const have = new Set((cur as { person_id: string }[]).map((x) => x.person_id));
      const want = new Set(d.people);
      const add = d.people.filter((p) => !have.has(p)).map((p) => ({ household_id: hid, event_id: eventId, person_id: p }));
      const remove = [...have].filter((p) => !want.has(p));
      if (add.length) { const { error } = await supabase.from("event_people").insert(add); if (error) throw error; }
      if (remove.length) { const { error } = await supabase.from("event_people").delete().eq("event_id", eventId!).in("person_id", remove); if (error) throw error; }
      return eventId!;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["events", hid] }); void qc.invalidateQueries({ queryKey: ["audit", hid] }); },
  });
}

export function useDeleteEvent() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, onlyDate }: { id: string; onlyDate?: string }) => {
      if (onlyDate) {
        const { error } = await supabase.from("event_exceptions").insert({ household_id: hid, event_id: id, occurs_on: onlyDate, cancelled: true });
        if (error) throw error;
      } else {
        const { error } = await supabase.from("calendar_events").update({ deleted_at: new Date().toISOString() }).eq("id", id);
        if (error) throw error;
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["events", hid] }); },
  });
}

/** CSS colour for a theme token like "chart-3". */
export const tokenColour = (t: string | undefined, alpha = 1) => `rgb(var(--${t ?? "primary"}) / ${alpha})`;

/** Open a place in the phone's maps app (Apple Maps on iPhone, Google elsewhere). */
export function mapsLink(place: string) {
  const q = encodeURIComponent(place);
  const apple = typeof navigator !== "undefined" && /iPhone|iPad|Macintosh/.test(navigator.userAgent);
  return apple ? `https://maps.apple.com/?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`;
}

/**
 * Move an occurrence (drag and drop, FF-026).
 *   mode "all": the whole series shifts by the same amount.
 *   mode "one": this date is cancelled on the series and a one-off copy is
 *               made at the new time (who it's for is copied too).
 * newStart is a Date for timed events; for all-day events only newDate matters.
 */
export function useMoveEvent() {
  const hid = useHouseholdId(); const me = useMe(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ occ, newDate, newStart, mode }: { occ: Occurrence; newDate: string; newStart?: Date; mode: "one" | "all" }) => {
      const e = occ.event;
      const dayShift = (parseISO(newDate).getTime() - parseISO(occ.date).getTime());
      const timedShift = !e.all_day && occ.start && newStart ? newStart.getTime() - occ.start.getTime() : dayShift;
      if (mode === "all" || e.repeat === "none") {
        const patch = e.all_day
          ? { start_date: ymd(new Date(parseISO(e.start_date!).getTime() + dayShift)) }
          : { starts_at: new Date(new Date(e.starts_at!).getTime() + timedShift).toISOString(), ends_at: e.ends_at ? new Date(new Date(e.ends_at).getTime() + timedShift).toISOString() : null };
        const { error } = await supabase.from("calendar_events").update(patch).eq("id", e.id);
        if (error) throw error;
        return;
      }
      const { error: x1 } = await supabase.from("event_exceptions").insert({ household_id: hid, event_id: e.id, occurs_on: occ.date, cancelled: true });
      if (x1) throw x1;
      const dur = occ.start && occ.end ? occ.end.getTime() - occ.start.getTime() : 0;
      const start = newStart ?? (occ.start ? new Date(occ.start.getTime() + dayShift) : null);
      const { data, error: x2 } = await supabase.from("calendar_events").insert({
        household_id: hid, title: e.title, notes: e.notes, location: e.location, all_day: e.all_day,
        start_date: e.all_day ? newDate : null, starts_at: e.all_day ? null : start!.toISOString(),
        ends_at: e.all_day || !dur ? null : new Date(start!.getTime() + dur).toISOString(),
        repeat: "none", created_by: me?.id ?? null,
      }).select("id").single();
      if (x2) throw x2;
      const id = (data as { id: string }).id;
      if (occ.people.length) {
        const { error: x3 } = await supabase.from("event_people").insert(occ.people.map((p) => ({ household_id: hid, event_id: id, person_id: p })));
        if (x3) throw x3;
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["events", hid] }); void qc.invalidateQueries({ queryKey: ["audit", hid] }); },
  });
}
